/**
 * GraphQL Photo Version Hooks
 *
 * Hooks for photo version operations: upload, view history.
 * Feature: 081-photo-versioning
 */

import { useMutation, useQuery, gql } from '@apollo/client';
import logger from '../../utils/logger';
import type { ImageVariant } from '../../components/photos/AdaptivePhoto';

// GraphQL Queries
const GET_PHOTO_VERSIONS_QUERY = gql`
  query GetPhotoVersions($photoId: ID!) {
    photoVersions(photoId: $photoId) {
      id
      globalId
      photoId
      versionNumber
      isCurrent
      storageKey
      fileSize
      width
      height
      mimeType
      fileHash
      notes
      createdAt
      thumbnailUrl
      previewUrl
      fullUrl
      variants {
        id
        variantType
        storageUrl
        publicUrl
        width
        height
        fileSize
        format
      }
    }
  }
`;

// GraphQL Mutations
const GET_VERSION_UPLOAD_URL_MUTATION = gql`
  mutation GetVersionUploadUrl($input: GetVersionUploadUrlInput!) {
    getVersionUploadUrl(input: $input) {
      uploadUrl
      storageKey
      expiresAt
    }
  }
`;

const CREATE_PHOTO_VERSION_MUTATION = gql`
  mutation CreatePhotoVersion($input: CreatePhotoVersionInput!) {
    createPhotoVersion(input: $input) {
      success
      version
      errorCode
      errorMessage
    }
  }
`;

// Types
export interface PhotoVersion {
  id: string;
  globalId: string;
  photoId: string;
  versionNumber: number;
  isCurrent: boolean;
  storageKey: string;
  fileSize: number | null;
  width: number | null;
  height: number | null;
  mimeType: string | null;
  fileHash: string | null;
  notes: string | null;
  createdAt: string;
  thumbnailUrl: string | null;
  previewUrl: string | null;
  fullUrl: string | null;
  /** Image variants for adaptive resolution selection */
  variants: ImageVariant[];
}

interface VersionUploadUrlPayload {
  uploadUrl: string;
  storageKey: string;
  expiresAt: string;
}

interface CreatePhotoVersionPayload {
  success: boolean;
  version: {
    id: string;
    globalId: string;
    photoId: string;
    versionNumber: number;
    isCurrent: boolean;
    storageKey: string;
    fileSize: number | null;
    width: number | null;
    height: number | null;
    mimeType: string | null;
    notes: string | null;
    createdAt: string;
  } | null;
  errorCode: string | null;
  errorMessage: string | null;
}

// Input types
interface GetVersionUploadUrlInput {
  photoId: string;
  filename: string;
  contentType?: string;
  fileSize?: number;
}

interface CreatePhotoVersionInput {
  photoId: string;
  storageKey: string;
  fileSize?: number;
  width?: number;
  height?: number;
  mimeType?: string;
  fileHash?: string;
  notes?: string;
}

/**
 * T042: Hook for fetching all versions of a photo
 */
export function usePhotoVersions(photoId: string | null) {
  const { data, loading, error, refetch } = useQuery<
    { photoVersions: PhotoVersion[] },
    { photoId: string }
  >(GET_PHOTO_VERSIONS_QUERY, {
    variables: { photoId: photoId! },
    skip: !photoId,
    fetchPolicy: 'cache-and-network',
  });

  return {
    versions: data?.photoVersions ?? [],
    loading,
    error,
    refetch,
  };
}

/**
 * Hook for getting a pre-signed upload URL for a new version
 */
export function useGetVersionUploadUrlGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { getVersionUploadUrl: VersionUploadUrlPayload },
    { input: GetVersionUploadUrlInput }
  >(GET_VERSION_UPLOAD_URL_MUTATION);

  const getVersionUploadUrl = async (input: GetVersionUploadUrlInput) => {
    logger.info('PhotoVersions', 'Getting version upload URL via GraphQL', {
      photoId: input.photoId,
      filename: input.filename,
    });

    const result = await mutate({ variables: { input } });

    if (result.data?.getVersionUploadUrl) {
      logger.info('PhotoVersions', 'Got version upload URL', {
        storageKey: result.data.getVersionUploadUrl.storageKey,
      });
    }

    return result.data?.getVersionUploadUrl;
  };

  return {
    getVersionUploadUrl,
    data: data?.getVersionUploadUrl,
    loading,
    error,
  };
}

/**
 * Hook for creating a new photo version after upload
 */
export function useCreatePhotoVersionGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { createPhotoVersion: CreatePhotoVersionPayload },
    { input: CreatePhotoVersionInput }
  >(CREATE_PHOTO_VERSION_MUTATION);

  const createPhotoVersion = async (input: CreatePhotoVersionInput) => {
    logger.info('PhotoVersions', 'Creating photo version via GraphQL', {
      photoId: input.photoId,
      storageKey: input.storageKey,
    });

    const result = await mutate({
      variables: { input },
      // Refetch queries that display photo data to ensure they show the new version
      // GetPhoto: Individual photo pages
      // GetPhotoVersions: Version history panel
      // GetCollection: Collection editor - displays photos with their current variants
      refetchQueries: ['GetPhoto', 'GetPhotoVersions', 'GetCollection'],
    });

    const payload = result.data?.createPhotoVersion;

    if (payload?.success) {
      logger.info('PhotoVersions', 'Photo version created', {
        versionNumber: payload.version?.versionNumber,
      });
    } else if (payload?.errorCode) {
      logger.warn('PhotoVersions', 'Photo version creation failed', {
        errorCode: payload.errorCode,
        errorMessage: payload.errorMessage,
      });
    }

    return payload;
  };

  return {
    createPhotoVersion,
    data: data?.createPhotoVersion,
    loading,
    error,
  };
}

/**
 * Combined hook for uploading a new photo version
 * Handles the full flow: get upload URL, upload file, create version record
 */
export function useUploadPhotoVersion() {
  const { getVersionUploadUrl, loading: urlLoading, error: urlError } = useGetVersionUploadUrlGraphQL();
  const { createPhotoVersion, loading: createLoading, error: createError } = useCreatePhotoVersionGraphQL();

  const uploadPhotoVersion = async (
    photoId: string,
    file: File,
    options?: {
      notes?: string;
      onUploadProgress?: (progress: number) => void;
    }
  ): Promise<CreatePhotoVersionPayload | null> => {
    try {
      // Step 1: Get upload URL
      const uploadUrlData = await getVersionUploadUrl({
        photoId,
        filename: file.name,
        contentType: file.type,
        fileSize: file.size,
      });

      if (!uploadUrlData) {
        logger.error('PhotoVersions', 'Failed to get upload URL');
        return null;
      }

      // Step 2: Compute file hash (optional, for duplicate detection)
      let fileHash: string | undefined;
      try {
        const arrayBuffer = await file.arrayBuffer();
        const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        fileHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      } catch {
        logger.warn('PhotoVersions', 'Failed to compute file hash, proceeding without duplicate detection');
      }

      // Step 3: Upload file to storage with progress tracking
      logger.info('PhotoVersions', 'Uploading file to storage', {
        storageKey: uploadUrlData.storageKey,
        fileSize: file.size,
      });

      // Use XMLHttpRequest for progress events
      const uploadSuccess = await new Promise<boolean>((resolve) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable && options?.onUploadProgress) {
            const progress = Math.round((event.loaded / event.total) * 100);
            options.onUploadProgress(progress);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(true);
          } else {
            logger.error('PhotoVersions', 'File upload failed', {
              status: xhr.status,
              statusText: xhr.statusText,
            });
            resolve(false);
          }
        };

        xhr.onerror = () => {
          logger.error('PhotoVersions', 'File upload network error');
          resolve(false);
        };

        xhr.open('PUT', uploadUrlData.uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      if (!uploadSuccess) {
        return null;
      }

      logger.info('PhotoVersions', 'File uploaded successfully');

      // Step 4: Get image dimensions (if possible)
      let width: number | undefined;
      let height: number | undefined;
      try {
        const dimensions = await getImageDimensions(file);
        width = dimensions.width;
        height = dimensions.height;
      } catch {
        logger.warn('PhotoVersions', 'Failed to get image dimensions');
      }

      // Step 5: Create version record
      const versionResult = await createPhotoVersion({
        photoId,
        storageKey: uploadUrlData.storageKey,
        fileSize: file.size,
        width,
        height,
        mimeType: file.type,
        fileHash,
        notes: options?.notes,
      });

      return versionResult ?? null;
    } catch (err) {
      logger.error('PhotoVersions', 'Upload photo version failed', { error: err });
      return null;
    }
  };

  return {
    uploadPhotoVersion,
    loading: urlLoading || createLoading,
    error: urlError || createError,
  };
}

/**
 * Helper to get image dimensions
 */
function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(file);
  });
}
